/* eslint-disable react-refresh/only-export-components -- The canonical component entrypoint also exports its sealed descriptor vocabulary and validator. */
import React from 'react';
import { FmButton } from './FmButton';
import { FmChip, type FmChipVariant } from './FmChip';
import { FmHeading } from './FmComposition';
import type {
  FmEvidenceClaim,
  FmEvidenceComparisonValue,
  FmEvidenceDescriptor,
  FmEvidenceProps,
  EvidenceScalar,
} from './FmEvidence.types';
import { assertFmEvidenceDescriptor } from './FmEvidence.validation';
import LanguageContext, { EMERGENCY_FALLBACK_TRANSLATIONS } from '@/i18n/LanguageContext';
import type { CommonTranslations } from '@/i18n/types';
import type { Language } from '@/i18n/types';
import './FmEvidence.css';

export type {
  FmEvidenceAvailability,
  FmEvidenceBandDescriptor,
  FmEvidenceBasis,
  FmEvidenceCitation,
  FmEvidenceClaim,
  FmEvidenceComparisonRow,
  FmEvidenceComparisonValue,
  FmEvidenceDates,
  FmEvidenceDescriptor,
  FmEvidenceExpertDisclosure,
  FmEvidenceFieldProvenance,
  FmEvidenceFreshness,
  FmEvidenceMissingness,
  FmEvidenceOwnerAction,
  FmEvidenceOwnerContext,
  FmEvidenceProps,
  FmEvidenceRestriction,
  FmEvidenceRowsDescriptor,
  EvidenceScalar,
  FmEvidenceSource,
  FmEvidenceSourceComparisonDescriptor,
  FmEvidenceTimestamp,
} from './FmEvidence.types';
export { assertFmEvidenceDescriptor } from './FmEvidence.validation';

type EvidenceLabels = CommonTranslations['ui']['evidence'];

function chipVariant(status: string): FmChipVariant {
  if (['available', 'complete'].includes(status)) return 'success';
  if (['partial', 'stale', 'restricted', 'error'].includes(status)) return 'warn';
  if (['current'].includes(status)) return 'info';
  return 'mute';
}

type EvidenceStatusValue = keyof EvidenceLabels['status'];

/**
 * The owner-facing word for a served status. The descriptor's optional `label`
 * wins only when the source supplied copy of its own; otherwise the status enum
 * is localized here, so the pane can never mix Finnish field names with English
 * values.
 */
function statusText(item: { status: string; label?: string }, labels: EvidenceLabels): string {
  if (item.label !== undefined) return item.label;
  const word = labels.status[item.status as EvidenceStatusValue];
  if (word === undefined) {
    throw new Error(`FmEvidence: the locale has no word for status "${item.status}".`);
  }
  return word;
}

function valueText(
  value: EvidenceScalar,
  labels: EvidenceLabels,
  language: Language,
  unit?: string,
  unavailableLabel?: string,
): string {
  if (value === null) return unavailableLabel ?? '';
  const rendered =
    typeof value === 'boolean'
      ? value
        ? labels.yes
        : labels.no
      : typeof value === 'number'
        ? new Intl.NumberFormat(language, { maximumFractionDigits: 20 }).format(value)
        : value;
  return unit ? `${rendered} ${unit}` : rendered;
}

const EvidenceStatusLegend: React.FC<{
  evidence: FmEvidenceDescriptor;
  labels: EvidenceLabels;
}> = ({ evidence, labels }) => {
  const items = [
    [labels.availability, evidence.availability],
    [labels.restriction, evidence.restriction],
    [labels.freshness, evidence.freshness],
    [labels.coverage, evidence.missingness],
  ] as const;
  return (
    <dl className="fm-evidence__status" aria-label={labels.statusLegend}>
      {items.map(([label, item]) => (
        <div key={label} data-state={item.status} data-reason-code={item.reasonCode}>
          <dt>{label}</dt>
          <dd>
            {/* A status word can be a short phrase, and these cells are narrow.
                The chip wraps and grows rather than clipping its own text. */}
            <FmChip variant={chipVariant(item.status)} multiline>
              {statusText(item, labels)}
            </FmChip>
            {item.detail ? <small>{item.detail}</small> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
};

function formatEvidenceDate(value: string, language: Language): string {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const parsed = new Date(dateOnly ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`FmEvidence: "${value}" is not a served date.`);
  }
  return new Intl.DateTimeFormat(language, {
    dateStyle: 'medium',
    ...(dateOnly ? { timeZone: 'UTC' } : { timeStyle: 'short' }),
  }).format(parsed);
}

/** A served status that needs the owner's attention; nominal ones stay in the disclosure. */
const NOMINAL_STATUS = new Set(['available', 'open', 'current', 'complete']);

/**
 * The one source line: basis, each source with its dataset, the served dates,
 * and only the statuses that need attention. Everything else waits behind the
 * disclosure, so the source is stated once and never as a card.
 */
const EvidenceSourceLine: React.FC<{
  evidence: FmEvidenceDescriptor;
  labels: EvidenceLabels;
  language: Language;
}> = ({ evidence, labels, language }) => {
  const dates = [
    [labels.observed, evidence.dates?.observedAt],
    [labels.retrieved, evidence.dates?.retrievedAt],
    [labels.effective, evidence.dates?.effectiveAt],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
  const attention = (
    [
      ['availability', evidence.availability],
      ['restriction', evidence.restriction],
      ['freshness', evidence.freshness],
      ['coverage', evidence.missingness],
    ] as const
  ).filter(([, item]) => !NOMINAL_STATUS.has(item.status));
  // Two dimensions can share a status word, so each chip names its dimension.
  const dimensionLabel = {
    availability: labels.availability,
    restriction: labels.restriction,
    freshness: labels.freshness,
    coverage: labels.coverage,
  };
  return (
    <p className="fm-evidence__source-line" aria-label={labels.sourcesAndCitations}>
      <span className="fm-evidence__basis">{labels.basis[evidence.basis]}</span>
      {evidence.sources.map((source) => (
        <span key={source.id} data-source-id={source.id} data-source-revision={source.revision}>
          <span className="fm-evidence__source-name">{source.label}</span> · {source.dataset}
        </span>
      ))}
      {dates.map(([label, date]) => (
        <time key={label} dateTime={date}>
          {label} {formatEvidenceDate(date, language)}
        </time>
      ))}
      {attention.map(([dimension, item]) => (
        <FmChip
          key={dimension}
          variant={chipVariant(item.status)}
          data-state={item.status}
          data-reason-code={item.reasonCode}
        >
          {dimensionLabel[dimension]}: {statusText(item, labels)}
        </FmChip>
      ))}
    </p>
  );
};

/** Citations grouped by source as plain lines, never bullets. */
const CitationList: React.FC<{
  evidence: FmEvidenceDescriptor;
  labels: EvidenceLabels;
}> = ({ evidence, labels }) =>
  evidence.citations.length ? (
    <dl className="fm-evidence__citations" aria-label={labels.sourcesAndCitations}>
      {evidence.sources
        .filter((source) => evidence.citations.some((citation) => citation.sourceId === source.id))
        .map((source) => (
          <div key={source.id} data-source-id={source.id}>
            <dt>
              {source.label}
              {source.basis === evidence.basis ? null : ` · ${labels.basis[source.basis]}`}
            </dt>
            {evidence.citations
              .filter((citation) => citation.sourceId === source.id)
              .map((citation) => (
                <dd key={citation.id} data-citation-id={citation.id}>
                  {citation.label}
                  {citation.reference ? ` · ${citation.reference}` : ''}
                </dd>
              ))}
          </div>
        ))}
    </dl>
  ) : null;

function provenanceSourceLabels(claim: FmEvidenceClaim, evidence: FmEvidenceDescriptor): string {
  return claim.provenance.sourceIds
    .map((sourceId) => evidence.sources.find((source) => source.id === sourceId)!.label)
    .join(' + ');
}

const ClaimRow: React.FC<{
  claim: FmEvidenceClaim;
  evidence: FmEvidenceDescriptor;
  labels: EvidenceLabels;
  language: Language;
}> = ({ claim, evidence, labels, language }) => (
  <div className="fm-evidence__row" data-field-id={claim.id}>
    <div>
      <strong>{claim.label}</strong>
      {claim.explanation ? <small>{claim.explanation}</small> : null}
    </div>
    <span>{valueText(claim.value, labels, language, claim.unit)}</span>
    {/* With one source the source line already names it; per-row attribution
        only earns its place when the rows draw on different sources. */}
    {evidence.sources.length > 1 && claim.provenance.sourceIds.length ? (
      <small>
        {provenanceSourceLabels(claim, evidence)} · {labels[claim.provenance.dateKind]}
      </small>
    ) : null}
  </div>
);

function comparisonValue(
  value: FmEvidenceComparisonValue,
  labels: EvidenceLabels,
  language: Language,
): string {
  return valueText(
    value.value,
    labels,
    language,
    value.unit,
    statusText(value.availability, labels),
  );
}

const SourceComparison: React.FC<{
  evidence: Extract<FmEvidenceDescriptor, { kind: 'source-comparison' }>;
  labels: EvidenceLabels;
  language: Language;
}> = ({ evidence, labels, language }) => (
  <div className="fm-evidence__comparison">
    <table>
      <caption>{evidence.expertDisclosure?.caption ?? evidence.title}</caption>
      <thead>
        <tr>
          <th scope="col">{labels.field}</th>
          {evidence.sources.map((source) => (
            <th key={source.id} scope="col">
              {source.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {evidence.rows.map((row) => (
          <tr key={row.id} data-field-id={row.id}>
            <th scope="row">{row.label}</th>
            {evidence.sources.map((source) => {
              const entry = row.values.find((value) => value.sourceId === source.id)!;
              return (
                <td
                  key={source.id}
                  data-label={source.label}
                  data-state={entry.availability.status}
                >
                  <span>{comparisonValue(entry, labels, language)}</span>
                  <small>{statusText(entry.availability, labels)}</small>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function fieldIds(evidence: FmEvidenceDescriptor): string[] {
  if (evidence.kind === 'evidence-band') return [evidence.claim.id];
  return evidence.rows.map((row) => row.id);
}

const EvidenceDetails: React.FC<{
  evidence: FmEvidenceDescriptor;
  labels: EvidenceLabels;
  language: Language;
}> = ({ evidence, labels, language }) => {
  if (evidence.kind === 'evidence-band') {
    return (
      <ClaimRow claim={evidence.claim} evidence={evidence} labels={labels} language={language} />
    );
  }
  if (evidence.kind === 'source-comparison') {
    return <SourceComparison evidence={evidence} labels={labels} language={language} />;
  }
  return (
    <div className="fm-evidence__rows">
      {evidence.rows.map((row) => (
        <ClaimRow
          key={row.id}
          claim={row}
          evidence={evidence}
          labels={labels}
          language={language}
        />
      ))}
    </div>
  );
};

/** Canonical source/date/availability evidence renderer for every page archetype. */
export const FmEvidence: React.FC<FmEvidenceProps> = ({
  evidence,
  'data-testid': dataTestId = `fm-evidence-${evidence.id}`,
}) => {
  assertFmEvidenceDescriptor(evidence);
  const languageContext = React.useContext(LanguageContext);
  const labels = (languageContext?.t ?? EMERGENCY_FALLBACK_TRANSLATIONS).common.ui.evidence;
  const language = languageContext?.language ?? 'en';
  const [expanded, setExpanded] = React.useState(false);
  const detailsId = `${dataTestId}-details`;
  const statusRole = evidence.availability.status === 'error' ? 'alert' : undefined;
  const details = <EvidenceDetails evidence={evidence} labels={labels} language={language} />;
  const disclosure = evidence.expertDisclosure
    ? {
        show: evidence.expertDisclosure.showLabel,
        hide: evidence.expertDisclosure.hideLabel,
        caption: evidence.expertDisclosure.caption,
      }
    : { show: labels.showDetails, hide: labels.hideDetails, caption: labels.statusLegend };
  return (
    <section
      className="fm-evidence"
      aria-label={evidence.ariaLabel}
      data-kind={evidence.kind}
      data-basis={evidence.basis}
      data-availability={evidence.availability.status}
      data-restriction={evidence.restriction.status}
      data-freshness={evidence.freshness.status}
      data-testid={dataTestId}
      role={statusRole}
    >
      <header className="fm-evidence__header">
        <FmHeading level={3}>{evidence.title}</FmHeading>
        <p>{evidence.summary}</p>
      </header>
      <EvidenceSourceLine evidence={evidence} labels={labels} language={language} />
      {evidence.expertDisclosure ? null : details}
      <div className="fm-evidence__disclosure">
        <FmButton
          variant="text"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => setExpanded((current) => !current)}
          data-testid={`${dataTestId}-toggle`}
        >
          {expanded ? disclosure.hide : disclosure.show}
        </FmButton>
        {expanded ? (
          <section id={detailsId} aria-label={disclosure.caption}>
            <EvidenceStatusLegend evidence={evidence} labels={labels} />
            {evidence.expertDisclosure ? details : null}
            <CitationList evidence={evidence} labels={labels} />
          </section>
        ) : null}
      </div>
      {evidence.ownerAction ? (
        <footer>
          <FmButton
            variant="bordered"
            onClick={() =>
              evidence.ownerAction?.onOpen({
                evidenceId: evidence.id,
                ownerPageId: evidence.ownerAction.ownerPageId,
                sourceIds: evidence.sources.map((source) => source.id),
                fieldIds: fieldIds(evidence),
              })
            }
          >
            {evidence.ownerAction.label}
          </FmButton>
        </footer>
      ) : null}
    </section>
  );
};

FmEvidence.displayName = 'FmEvidence';

export default FmEvidence;
